import React, { memo, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { Grid } from '@mui/material';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import Select from 'modules/views/shared/Select';
import Button from 'modules/views/shared/Button';
import MultiSelect from 'modules/views/shared/MultiSelect';
import Loader from 'modules/views/shared/Loader';
import WithBranding from 'modules/views/shared/WithBranding';
import Warning from 'modules/views/shared/Warning';
import Checkbox from 'modules/views/shared/Checkbox';
import WorkerStatusLoginModal, { WorkerStatusLoginModalType } from 'modules/views/shared/WorkerStatusLoginModal';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import {
  resetOnboardingState,
  fetchAssignedCompanies,
  resetAssignedCompanies,
  assignExistingWorkerToCompanies,
  getPositiveDrugTestResults,
  setOnboarding,
} from 'modules/state-mgmt/onboarding';
import {
  fetchCompanies,
  fetchProjects,
  fetchWhitelistedProjects,
  updateProfileFieldConstraintsMap,
  restoreAdminCache,
} from 'modules/state-mgmt/cache';
import { setLockedProject } from 'modules/state-mgmt/app';
import { fetchWorkerProjects } from 'modules/state-mgmt/workerData';
import { AuthService } from 'services/AuthService';
import useLoadAllData from 'hooks/useLoadAllData';
import { setTemp } from 'modules/state-mgmt/onboarding';

import moment from 'moment';

import { actionMapForDataLoaders, actionMapForOtherLoaders, ROUTES } from '../../../../constants';

import { getIncompleteWorkersByProject } from 'utils/indexedDB';
import { getFieldConfig, prettifyErrorResponse } from 'utils';
import { saveAdminContext, getAdminContext } from 'utils/adminContext';
import { useStyles } from './styles';
import { CompanyProjectStatus } from 'models/general';
import { IPositiveDrugTestResult } from 'models/drugTesting';

const packageJson = require('../../../../../package.json');

// Narrow shape of i18next's `t`; the full TFunction type is too deep to instantiate here.
type TranslateFn = (key: string, options?: Record<string, string>) => string;

interface IEmployerOption {
  label: string;
  value: string;
}

// CH-4347 — these messages are longer than the app-wide 5s default and a worker may have
// several to read, so they are held for 10s.
const POSITIVE_DRUG_TEST_TOAST_DURATION_MS = 10000;

/**
 * CH-4347 — turns the positive drug test results into one message per returned entry. Each
 * entry is a project the worker holds a positive result on, so entries are rendered as-is
 * rather than grouped. Mirrors fca-operations-web's WorkerModal: a result blocks while its
 * validity has not passed, and `isAllowedToBeAccepted` only decides whether the copy names
 * the date the result stays valid until.
 */
const getBlockingDrugTestMessages = (results: IPositiveDrugTestResult[], t: TranslateFn): string[] =>
  (results || [])
    .filter(result => !result.isValidityPassed)
    .map(result => {
      if (!result.isAllowedToBeAccepted) {
        return t('selectProject.errors.positiveDrugTest', { projectName: result.projectName });
      }
      const validUntil = result.positiveResultValidityInMonths
        ? moment(result.resultAddedAt).add(result.positiveResultValidityInMonths, 'months').format('MMMM D, YYYY')
        : moment(result.resultAddedAt).format('MMMM D, YYYY');
      return t('selectProject.errors.positiveDrugTestWithValidity', {
        projectName: result.projectName,
        validUntil,
      });
    });

const SelectProject = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user, loader, onboarding, cache, isOffline, workerProjects, whitelistedProjectIds, lockedProject } =
    useAppSelector(state => ({
      user: state.user,
      loader: state.loader,
      onboarding: state.onboarding,
      cache: state.cache,
      app: state.app,
      isOffline: !state.app.isOnline,
      workerProjects: state.workerData.workerProjects,
      whitelistedProjectIds: state.cache.whitelistedProjectIds,
      lockedProject: state.app.lockedProject,
    }));

  const [{ isLoading, hasLoaded, errors: loadErrors }, loadAllData] = useLoadAllData();
  const freezEmployers = useRef<boolean>(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);

  const projectIdFromParams = searchParams.get('projectId');
  const projectNameFromParams = searchParams.get('projectName');
  const addEmployerFlow = (location.state as any)?.addEmployerFlow || false;

  // Load saved admin context asynchronously from IndexedDB
  const [adminContext, setAdminContext] = useState<any>(null);
  const [isAdminContextLoading, setIsAdminContextLoading] = useState<boolean>(!user.isWorker && !addEmployerFlow);

  useEffect(() => {
    if (user.isWorker || addEmployerFlow) return;
    getAdminContext()
      .then(ctx => {
        if (
          ctx?.cache?.project?.id &&
          cache.project?.id !== ctx.cache.project.id &&
          cache.projects.some((p: any) => p.id === ctx.cache.project.id)
        ) {
          setAdminContext(ctx);
        }
        setIsAdminContextLoading(false);
      })
      .catch(() => setIsAdminContextLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const defaultProject = React.useMemo(() => {
    // Admin context takes priority when cache was overwritten by a worker session
    if (adminContext?.cache?.project?.id) {
      return {
        label: adminContext.cache.project.name,
        value: adminContext.cache.project.id,
      };
    }

    // QR-code URL param overrides locked project for this worker's session
    if (projectIdFromParams) {
      return { label: projectNameFromParams, value: projectIdFromParams };
    }

    // Locked project restores when returning to SelectProject without a QR scan
    if (lockedProject) {
      return lockedProject;
    }

    const projectFromCache = cache.project?.id
      ? {
          label: cache.project.name,
          value: cache.project.id,
        }
      : null;

    const projectFromTemp = onboarding?.temp?.project || null;

    return projectFromCache || projectFromTemp;
  }, [
    adminContext,
    cache.project,
    projectIdFromParams,
    projectNameFromParams,
    lockedProject,
    onboarding?.temp?.project,
  ]);

  const [project, setProject] = useState<any>(defaultProject);
  const [employers, setEmployers] = useState<any>(addEmployerFlow ? onboarding?.temp?.employers || [] : []);
  const [disabledEmployerIds, setDisabledEmployerIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<any>({
    project: '',
    employer: '',
  });
  const [incompleteWorkersWarning, setIncompleteWorkersWarning] = useState<React.ReactNode>('');
  const [loginWarning, setLoginWarning] = useState<string>('');
  const [projectNotWhitelistedWarning, setProjectNotWhitelistedWarning] = useState<boolean>(false);
  const [isWorkerProjectsLoading, setIsWorkerProjectsLoading] = useState<boolean>(false);
  const [isCheckingDrugTestResults, setIsCheckingDrugTestResults] = useState<boolean>(false);
  const [isIncompleteWorkersCheckPending, setIsIncompleteWorkersCheckPending] = useState<boolean>(
    !user.isWorker && !!cache.project?.id,
  );

  // Dispatch Redux cache restore for the admin context
  useEffect(() => {
    if (adminContext) {
      dispatch(restoreAdminCache(adminContext.cache));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch worker projects for logged-in workers
  useEffect(() => {
    if (user.isWorker && user.workerId) {
      setIsWorkerProjectsLoading(true);
      dispatch(
        fetchWorkerProjects({
          workerId: user.workerId,
          languageCode: 'en',
        }),
      ).finally(() => {
        setIsWorkerProjectsLoading(false);
      });
    }
  }, [user.isWorker, user.workerId, dispatch]);

  const onChangeProject = (newProject: any) => {
    setProject(newProject);
    setEmployers([]);
    setDisabledEmployerIds([]);
  };

  const onChangeEmployers = (newEmployers: any) => {
    // Ensure disabled employers are always included
    const disabledEmployers = employers.filter(emp => disabledEmployerIds.includes(emp.value));
    const newEmployersFiltered = newEmployers.filter(emp => !disabledEmployerIds.includes(emp.value));
    setEmployers([...disabledEmployers, ...newEmployersFiltered]);
  };

  useEffect(() => {
    dispatch(resetAssignedCompanies());

    if (addEmployerFlow) {
      if (isOffline && cache.project?.id === project?.value && (!user.userId || !user.isWorker)) return;
      dispatch(
        fetchAssignedCompanies({
          projectId: projectIdFromParams || '',
          languageCode: 'en',
          workerId: onboarding.id || (user.isWorker ? user.workerId : ''),
        }),
      );
    } else if (user.isAdmin) {
      // Only reset onboarding state when the user is an admin
      dispatch(resetOnboardingState());
    }
  }, [addEmployerFlow, projectIdFromParams, onboarding.id, dispatch, isOffline, user]);

  useEffect(() => {
    if (user.userId && !user.isWorker && cache.project?.id) {
      getIncompleteWorkersByProject(cache.project.id)
        .then(incompleteWorkers => {
          if (incompleteWorkers.length > 0) {
            if (project?.value !== cache.project.id) {
              setProject({
                label: cache.project.name,
                value: cache.project.id,
              });
            }
            setIncompleteWorkersWarning(
              <Trans
                i18nKey="selectProject.warnings.incompleteWorkers"
                values={{ projectName: cache.project.name }}
                components={{ bold: <span style={{ fontWeight: '600' }} /> }}
              />,
            );
          }
          setIsIncompleteWorkersCheckPending(false);
        })
        .catch(error => {
          setIsIncompleteWorkersCheckPending(false);
          enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
        });
    } else {
      setIsIncompleteWorkersCheckPending(false);
    }
  }, [cache.project, user.isWorker, getIncompleteWorkersByProject, setProject, setIncompleteWorkersWarning]);

  useEffect(() => {
    // Show warning if no logged-in user and no project in URL
    if (!user?.userId && !projectIdFromParams) {
      setLoginWarning(t('selectProject.warnings.loginRequired'));
    } else {
      setLoginWarning('');
    }
  }, [user?.userId, projectIdFromParams, t]);

  useEffect(() => {
    if (projectIdFromParams && whitelistedProjectIds?.length > 0) {
      setProjectNotWhitelistedWarning(!whitelistedProjectIds.includes(projectIdFromParams));
    }
  }, [projectIdFromParams, whitelistedProjectIds]);

  useEffect(() => {
    // Guard against a worker deep-linking here for a project they have only been invited to —
    // they must go through Register on the project list instead. It must not fire for
    // addEmployerFlow, which is reached deliberately from a *current* project card.
    //
    // A worker can hold assignments for the same project under several companies, so the same
    // projectId legitimately appears in both `pending` and `current` (accepted with company A,
    // invited with company B). Matching on projectId alone therefore bounced Add Employer back
    // to the project list for those projects, while working for projects that were only current.
    if (projectIdFromParams && user.isWorker && workerProjects && !addEmployerFlow) {
      const isPendingProject = workerProjects?.pending?.some((p: any) => p.project?.id === projectIdFromParams);
      const isCurrentProject = workerProjects?.current?.some((p: any) => p.project?.id === projectIdFromParams);
      if (isPendingProject && !isCurrentProject) {
        navigate(ROUTES.PROJECT_LIST.path);
      }
    }
  }, [user, projectIdFromParams, workerProjects, navigate, addEmployerFlow]);

  useEffect(() => {
    if (isOffline && cache.projects?.length > 0 && (!user.userId || !user.isWorker)) return;

    !isOffline && dispatch(fetchWhitelistedProjects());

    AuthService.getSessionSafe().then(session => {
      if (session?.getIdToken?.()?.getJwtToken?.()) {
        // For logged-in workers, only fetch projects if there's a projectId in URL
        // Otherwise, they'll see their pending projects from workerProjects state
        if (!user.isWorker || projectIdFromParams) {
          dispatch(fetchProjects());
        }
      }
    });
  }, [user, dispatch, isOffline, projectIdFromParams]);

  useEffect(() => {
    if (isIncompleteWorkersCheckPending) return;
    if (isOffline && cache.companies?.['en']?.length > 0 && (!user.userId || !user.isWorker)) return;

    if (project?.value) {
      dispatch(
        fetchCompanies({
          projectId: project?.value,
          languageCode: 'en',
        }),
      );
    }
  }, [project, dispatch, isOffline, user, isIncompleteWorkersCheckPending]);

  useEffect(() => {
    if (addEmployerFlow) {
      const assignedCompanpies = onboarding?.assignedCompanies || [];
      if (assignedCompanpies.length > 0 && cache.companies?.['en']?.length > 0 && !freezEmployers.current) {
        const companyIds = assignedCompanpies.map(c => c.id);
        const preSelectedEmployers = cache.companies['en']
          .filter(c => companyIds.includes(c.id))
          .map(c => ({
            label: c.name,
            value: c.id,
          }));
        if (preSelectedEmployers.length > 0) {
          setEmployers(preSelectedEmployers);
          setDisabledEmployerIds(companyIds);
        }
      }
    }
  }, [cache.companies, onboarding.assignedCompanies, addEmployerFlow]);

  useEffect(() => {
    if ((hasLoaded || loadErrors.length) && !isLoading) {
      if (loadErrors.length) {
        enqueueSnackbar(loadErrors?.map(err => prettifyErrorResponse(err))?.join('\n'), {
          variant: 'error',
          style: { whiteSpace: 'pre-line' },
        });
      } else {
        dispatch(
          setTemp({
            project,
            employers,
            ...(addEmployerFlow ? { addEmployerFlow: true } : {}),
          }),
        );

        // Setting up calculated profile field constraints map for selected companies
        if (cache.projectProfileCustomFields?.length > 0 && cache.workerProfileFieldsForAllCompanies?.length > 0) {
          const workerProfileCustomFields = getFieldConfig(
            cache.workerProfileFieldsForAllCompanies || [],
            cache.projectProfileCustomFields || [],
            employers.map((e: any) => e.value),
          );
          dispatch(updateProfileFieldConstraintsMap(workerProfileCustomFields));
        }

        // Save admin project context so it persists across worker login interruptions
        if (!user.isWorker && !addEmployerFlow) {
          saveAdminContext(cache);
        }

        if (cache.workerProfileFieldsForAllCompanies?.length > 0) {
          // Assign the existing worker to the selected companies. Two entry points need this:
          //  - addEmployerFlow: worker adds/registers employers from ProjectList (router state set).
          //  - A logged-in worker who reached this flow via a direct invite URL, which carries no
          //    addEmployerFlow router state. Without assigning here, the worker is never linked to
          //    the selected employer and acceptProject fails with a bad request at the consent step.
          const isLoggedInWorkerInvite = user.isWorker && !!user.workerId;
          if ((addEmployerFlow || isLoggedInWorkerInvite) && onboarding.id) {
            if (!isOffline) {
              dispatch(
                assignExistingWorkerToCompanies({
                  projectId: project.value,
                  employers,
                  languageCode: 'en',
                }),
              )
                .unwrap()
                .then(() => {
                  navigate(ROUTES.PERSONAL_INFORMATION.path);
                })
                .catch(err => {
                  enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' });
                });
              return;
            } else {
              dispatch(
                setOnboarding({
                  uploadingDetails: {
                    ...(onboarding.uploadingDetails || {}),
                    isOffline: true,
                    pendingUploads: {
                      ...(onboarding.uploadingDetails?.pendingUploads || {}),
                      employers: true,
                    },
                  },
                }),
              );
            }
          }

          // Navigate to Personal Information for workers, Basic Information for admins
          if (addEmployerFlow || user.isWorker) {
            navigate(ROUTES.PERSONAL_INFORMATION.path);
          } else {
            navigate(ROUTES.BASIC_INFORMATION.path);
          }
        }
      }
    }
  }, [hasLoaded, loadErrors, isLoading, cache.workerProfileFieldsForAllCompanies, cache.projectProfileCustomFields]);

  // The lock only reads as active while the selected project is the locked one. A project URL
  // pointing somewhere else overrides the lock for that session, so the checkbox shows unchecked.
  const isLockActive = !!lockedProject && lockedProject.value === project?.value;

  const onClickLockCheckbox = () => {
    if (!isLockActive && !project?.value) {
      setErrors(prev => ({ ...prev, project: t('selectProject.errors.projectRequired') }));
      return;
    }
    setLockModalOpen(true);
  };

  const onLockPinSuccess = () => {
    dispatch(setLockedProject(isLockActive ? null : project));
    setLockModalOpen(false);
  };

  const onGetStarted = async () => {
    if (!project?.value) {
      setErrors(prev => ({
        ...prev,
        project: t('selectProject.errors.projectRequired'),
      }));
    } else {
      setErrors(prev => ({ ...prev, project: '' }));
    }
    if (!employers?.length) {
      setErrors(prev => ({
        ...prev,
        employer: t('selectProject.errors.employersRequired'),
      }));
    } else {
      setErrors(prev => ({ ...prev, employer: '' }));
    }
    if (project?.value && employers?.length) {
      if (
        isOffline &&
        ((cache.project?.id !== project?.value && (!user.userId || !user.isWorker)) || addEmployerFlow)
      ) {
        enqueueSnackbar(t('common.offlineError'), { variant: 'error' });
        return;
      }

      // CH-4347 — a worker with an active positive drug test on record cannot onboard onto
      // this project. Covers both the new-project and the add-employer flow, since they share
      // this handler. `onboarding.id || user.workerId` resolves a direct worker login and a
      // worker logged in inside an admin session; an admin creating a brand-new worker has no
      // worker id yet, so there is nothing to check. Skipped offline — the API is unreachable,
      // and the backend still enforces the rule at assign/register (FN-5722).
      const workerId = onboarding.id || user.workerId;
      if (workerId && !isOffline) {
        setIsCheckingDrugTestResults(true);
        try {
          const results = await dispatch(
            getPositiveDrugTestResults({
              workerId,
              projectId: project.value,
              companyIds: employers.map((e: IEmployerOption) => e.value),
              languageCode: 'en',
            }),
          ).unwrap();

          const blockingMessages = getBlockingDrugTestMessages(results, t);
          if (blockingMessages.length > 0) {
            // Hard block: the selection is left untouched so the worker can change employers.
            blockingMessages.forEach(message =>
              enqueueSnackbar(message, {
                variant: 'error',
                autoHideDuration: POSITIVE_DRUG_TEST_TOAST_DURATION_MS,
              }),
            );
            return;
          }
        } catch (err) {
          // Surface the failure but let the worker through — the backend is the enforcement point.
          enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' });
        } finally {
          setIsCheckingDrugTestResults(false);
        }
      }

      // Only freeze the add-employer pre-selection once the gate has passed, so a blocked
      // worker returning to the dropdown still sees their assigned employers repopulated.
      freezEmployers.current = true;
      loadAllData({
        projectId: project.value,
        employers: employers.map((e: any) => e.value),
        // skipWorkerApis: !user.isWorker,
        workerId: user.workerId,
      });
    }
  };

  // Determine which projects to show in dropdown
  const availableProjects = React.useMemo(() => {
    // For logged-in workers without projectId in URL, show only pending projects
    if (user.isWorker && !projectIdFromParams && workerProjects?.pending?.length > 0) {
      return workerProjects.pending.map((p: any) => ({
        label: p.project?.name,
        value: p.project?.id,
      }));
    }
    // Otherwise, show all projects from cache, filtered by whitelist when available
    return (
      cache.projects
        ?.filter?.((p: any) => !whitelistedProjectIds?.length || whitelistedProjectIds.includes(p.id))
        ?.map?.((p: any) => ({
          label: p.name,
          value: p.id,
        })) || []
    );
  }, [user.isWorker, projectIdFromParams, workerProjects, cache.projects, whitelistedProjectIds]);

  // Clear the lock when the locked project is no longer in the available projects list
  useEffect(() => {
    if (lockedProject && availableProjects.length > 0) {
      const stillExists = availableProjects.some(p => p.value === lockedProject.value);
      if (!stillExists) {
        dispatch(setLockedProject(null));
      }
    }
  }, [availableProjects, lockedProject, dispatch]);

  // Determine if project dropdown should be disabled
  const isProjectDropdownDisabled = React.useMemo(() => {
    if (!!incompleteWorkersWarning || !!loginWarning || !!projectNotWhitelistedWarning || isOffline) return true;

    // While a lock is in place the dropdown stays frozen — both when the locked project is the
    // one shown, and when a project URL has overridden it for this session. Changing projects
    // requires unlocking with the PIN first.
    if (lockedProject) return true;

    // Always disable for unauthenticated users or workers
    if (!user?.userId || user.isWorker) return true;

    return false;
  }, [
    incompleteWorkersWarning,
    loginWarning,
    projectNotWhitelistedWarning,
    isOffline,
    lockedProject,
    user?.userId,
    user.isWorker,
  ]);

  const companyOptions = React.useMemo(() => {
    return (
      cache.companies?.['en']?.map?.(c => {
        const isSuspended = c.companyProjectStatus === CompanyProjectStatus.SUSPENDED;
        return {
          label: c.name,
          value: c.id,
          companyProjectStatus: c.companyProjectStatus,
          ...(isSuspended && {
            statusChip: {
              label: user.isFcaUser
                ? t('selectProject.employerStatus.suspended')
                : t('selectProject.employerStatus.notAvailable'),
              variant: user.isFcaUser ? 'red' : 'gray',
            },
          }),
        };
      }) || []
    );
  }, [cache.companies, user.isFcaUser, t]);

  return isLoading ||
    isAdminContextLoading ||
    isWorkerProjectsLoading ||
    isCheckingDrugTestResults ||
    loader.dataLoaders?.[actionMapForDataLoaders.fetchProjects]?.isLoading ||
    loader.dataLoaders?.[actionMapForDataLoaders.fetchWhitelistedProjects]?.isLoading ||
    loader.dataLoaders?.[actionMapForDataLoaders.fetchWorkerProfileCustomFields]?.isLoading ||
    loader.otherLoaders?.[actionMapForOtherLoaders.assignExistingWorkerToCompanies]?.isLoading ? (
    <Loader />
  ) : (
    <WithBranding
      hasLoginButton={true}
      Content={
        <>
          <Grid container>
            <Grid item xs={12} lg={12}>
              <div className={classes.fieldParent}>
                <ContentHeader
                  title={t('selectProject.project')}
                  subtitle={!projectIdFromParams && t('selectProject.selectYourProject')}
                  styleClass={classes.fieldHeader}
                />
                {incompleteWorkersWarning && <Warning text={incompleteWorkersWarning} styleClass={classes.warning} />}
                {loginWarning && <Warning text={loginWarning} styleClass={classes.warning} />}
                {projectNotWhitelistedWarning && (
                  <Warning text={t('selectProject.warnings.projectNotEnabled')} styleClass={classes.projectWarning} />
                )}
                {projectIdFromParams && !incompleteWorkersWarning && projectNotWhitelistedWarning ? (
                  <p className={classes.projectName}>{projectNameFromParams}</p>
                ) : (
                  <Select
                    inputLabel={t('selectProject.selectProject')}
                    options={availableProjects}
                    selectedValue={project}
                    required={true}
                    error={errors.project}
                    onChange={onChangeProject}
                    disabled={isProjectDropdownDisabled}
                  />
                )}
                {user.isAdmin && !user.isWorker && !addEmployerFlow && (
                  <Checkbox
                    checked={isLockActive}
                    label={t('selectProject.lockSelectedProject')}
                    disabled={!isLockActive && !project?.value}
                    styleClass={classes.lockCheckbox}
                    onChange={onClickLockCheckbox}
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={12} lg={12}>
              <div className={classes.employerFieldParent}>
                <ContentHeader
                  title={t('selectProject.employer')}
                  subtitle={t('selectProject.selectYourEmployer')}
                  styleClass={classes.fieldHeader}
                />
                <MultiSelect
                  placeholder={t('selectProject.selectEmployer')}
                  options={companyOptions}
                  selectedValues={employers}
                  required={true}
                  error={errors.employer}
                  disabled={
                    !project?.value ||
                    !!loginWarning ||
                    !!projectNotWhitelistedWarning ||
                    loader.dataLoaders?.[actionMapForDataLoaders.fetchCompaniesEn]?.isLoading ||
                    loader.dataLoaders?.[actionMapForDataLoaders.fetchCompaniesEs]?.isLoading
                  }
                  isOptionDisabled={option =>
                    disabledEmployerIds.includes(option.value) ||
                    option.companyProjectStatus === CompanyProjectStatus.SUSPENDED
                  }
                  onChange={onChangeEmployers}
                />
              </div>
            </Grid>
          </Grid>
          <div className={classes.buttonParent}>
            <Button
              label={t('selectProject.buttons.getStarted')}
              hasEndArrow={true}
              isLoading={false}
              styleClass={classes.button}
              onClick={onGetStarted}
              disabled={projectNotWhitelistedWarning}
            />
          </div>
          {!onboarding.id && (
            <>
              <p className={classes.orTextParent}>
                <span className={classes.orText}>{t('common.orSmall')}</span>
              </p>
              <Button
                label={t('login.buttons.login')}
                variant="outlined"
                styleClass={classes.loginButton}
                onClick={() => navigate(ROUTES.LOGIN.path)}
              />
              <div className={classes.buildInfo}>
                Build Version {packageJson.buildVersion} | Published on{' '}
                {packageJson.buildDate && moment(packageJson.buildDate).format('MMM DD, YYYY')}
              </div>
            </>
          )}
          {lockModalOpen && (
            <WorkerStatusLoginModal
              type={WorkerStatusLoginModalType.LOCK_PROJECT}
              onClose={() => setLockModalOpen(false)}
              onSuccess={onLockPinSuccess}
            />
          )}
        </>
      }
    />
  );
};

export default memo(SelectProject);
